import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { contentAPI, creatorPostsAPI } from '../services/api'
import type { ClipsSource, Content as ContentType, CreatorPost, Publication, RecapPlatform } from '../types'
import toast from 'react-hot-toast'
import ConfirmDialog from '../components/ConfirmDialog'
import LoadingScreen from '../components/LoadingScreen'
import DraftEditor from '../components/DraftEditor'
import PostedLinks from '../components/PostedLinks'
import PostsSummary from '../components/PostsSummary'
import PostYourselfDialog from '../components/PostYourselfDialog'
import RepurposeModal from '../components/RepurposeModal'
import Tooltip from '../components/Tooltip'
import { CONTENT_GLOSSARY } from '../config/glossary'
import { formatTime } from '../components/clips/clipUtils'
import { apiErrorMessage } from '../utils/apiError'
import { publishFailureText } from '../utils/publishFailure'
import { useAuthStore } from '../store/authStore'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import ResultsDisclaimer from '../components/ResultsDisclaimer'
import { RECORD_MISMATCH_WARNING, STAND_IN_MESSAGE } from '../utils/draftFlags'

/**
 * What the generator flagged on this post: a post from a call's receipt that
 * dropped or altered its recorded result, figures that trace to nothing in
 * its verified data, and a recap's wording that reads as after fees (recap
 * figures are before fees). Flags, not blocks: the creator checks and edits.
 * A stand-in draft (made while the AI was unreachable) is the exception: it
 * can't be posted until it's rewritten or generated again.
 */
const DraftFlags = ({ content }: { content: ContentType }) => {
  const untraced = content.untraced_figures ?? []
  const feeWording = content.fee_wording ?? []
  const recordMismatch = content.record_mismatch ?? []
  if (!content.stand_in && untraced.length === 0 && feeWording.length === 0 && recordMismatch.length === 0) return null
  return (
    <div
      className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
      role="note"
      data-testid="draft-flags"
    >
      {content.stand_in && (
        <p className="font-semibold" data-testid="flag-stand-in">
          {STAND_IN_MESSAGE}
        </p>
      )}
      {recordMismatch.length > 0 && (
        <p className="font-semibold" data-testid="flag-record-mismatch">
          {RECORD_MISMATCH_WARNING}. As recorded: {recordMismatch.join(', ')}.
        </p>
      )}
      {untraced.length > 0 && (
        <p data-testid="flag-untraced">
          Check before posting: {untraced.join(', ')} {untraced.length === 1 ? "isn't" : "aren't"} in the data this
          post was written from.
        </p>
      )}
      {feeWording.length > 0 && (
        <p data-testid="flag-fee-wording">
          Says &ldquo;{feeWording.join('”, “')}&rdquo;, but every recap figure is before fees.
        </p>
      )}
    </div>
  )
}

const renderMarkdown = (text: string) =>
  DOMPurify.sanitize(marked.parse(text, { async: false }))

const NETWORK_NAMES: Record<string, string> = {
  twitter: 'Twitter',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
}

/** "Published Sep 24, 2026 to Twitter", from the accounts that took the post. */
export const publishedLine = (content: ContentType): string => {
  const published = (content.publications ?? []).filter((p) => p.is_published)
  const accounts = published.map((p) => NETWORK_NAMES[p.platform_type ?? ''] ?? p.platform_type)
  const latest = published
    .map((p) => p.published_at)
    .filter((at): at is string => !!at)
    .sort()
    .pop()
  const date = latest
    ? new Date(latest).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null
  return ['Published', date, accounts.length ? `to ${accounts.join(', ')}` : null]
    .filter(Boolean)
    .join(' ')
}

/** Drafts and failed publishes first (still to act on), then published posts; newest first in each. */
export const draftsFirst = (contents: ContentType[]): ContentType[] => {
  const isDone = (c: ContentType) => c.status === 'published'
  return [...contents].sort((a, b) => {
    if (isDone(a) !== isDone(b)) return isDone(a) ? 1 : -1
    return (b.created_at ?? '').localeCompare(a.created_at ?? '')
  })
}

const cardDate = (content: ContentType): string =>
  content.recap_date ??
  (content.created_at
    ? new Date(content.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '')

const Content = () => {
  // Publishing posts to FinCreator's shared house accounts, so it's admin only.
  const isAdmin = useAuthStore((state) => !!state.user?.is_superuser)
  const [contents, setContents] = useState<ContentType[]>([])
  const [loading, setLoading] = useState(true)
  const [draftingRecap, setDraftingRecap] = useState(false)
  const [recapMessage, setRecapMessage] = useState<string | null>(null)
  const [recapPlatform, setRecapPlatform] = useState<RecapPlatform>('twitter')
  const [repurposeContent, setRepurposeContent] = useState<ContentType | null>(null)
  const [confirmingPublishId, setConfirmingPublishId] = useState<number | null>(null)
  const [publishingId, setPublishingId] = useState<number | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState<ContentType | null>(null)
  const [deleting, setDeleting] = useState(false)
  // Posts the creator made on their own accounts ("I posted it"), and the draft being posted.
  const [posts, setPosts] = useState<CreatorPost[]>([])
  const [postingContent, setPostingContent] = useState<ContentType | null>(null)
  // Cards are one line until opened. A recap just drafted opens, and so do the
  // drafts Create links here with (their ids arrive in the link's state).
  const location = useLocation()
  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set((location.state as { openIds?: number[] } | null)?.openIds ?? [])
  )

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const open = (id: number) => setExpanded((prev) => new Set(prev).add(id))

  useEffect(() => {
    loadContent()
    // Optional: the posted links show on the cards when they load; any failure means none.
    Promise.resolve()
      .then(() => creatorPostsAPI.list())
      .then((list) => list.length > 0 && setPosts(list))
      .catch(() => undefined)
  }, [])

  const loadContent = async () => {
    try {
      const data = await contentAPI.list({ limit: 50 })
      setContents(data)
    } catch (error) {
      toast.error('Failed to load content')
    } finally {
      setLoading(false)
    }
  }

  // Drafts only: the recap is saved for review and never published here.
  const handleDraftRecap = async () => {
    setDraftingRecap(true)
    setRecapMessage(null)
    try {
      const result = await contentAPI.dailyRecap(recapPlatform)
      if (result.drafted && result.content) {
        const draft = result.content
        setContents((prev) => [draft, ...prev])
        open(draft.id)
        setRecapMessage('Draft saved')
        toast.success('Draft saved')
      } else {
        setRecapMessage('No closes today.')
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to draft the recap'))
    } finally {
      setDraftingRecap(false)
    }
  }

  const handlePublish = async () => {
    if (confirmingPublishId === null) return

    const id = confirmingPublishId
    setPublishingId(id)
    try {
      const publications: Publication[] = await contentAPI.publish(id)
      setConfirmingPublishId(null)

      if (publications.length === 0) {
        toast.error(
          'No platforms are configured for publishing. Add an active platform with auto-publish enabled.'
        )
        return
      }

      const successCount = publications.filter((p) => p.is_published).length
      if (successCount === publications.length) {
        toast.success('Content published successfully!')
      } else if (successCount > 0) {
        toast.success(`Published to ${successCount} of ${publications.length} platform(s)`)
      } else {
        toast.error(publishFailureText(publications, 'Failed to publish content to any platform'))
      }

      loadContent()
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to publish content'))
    } finally {
      setPublishingId(null)
    }
  }

  const handleDelete = async () => {
    if (confirmingDelete === null) return

    const id = confirmingDelete.id
    setDeleting(true)
    try {
      await contentAPI.delete(id)
      setContents((prev) => prev.filter((c) => c.id !== id))
      setConfirmingDelete(null)
      toast.success('Content deleted')
    } catch (error) {
      toast.error('Failed to delete content')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return <LoadingScreen />
  }

  return (
    <div className="space-y-6">
      {/* Today's recap: a draft from the day's real signal closes */}
      <div className="bg-white rounded-lg shadow px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">Today's recap</h2>
            <p className="text-xs text-gray-500">
              From the engine's closes since 00:00 UTC, each by win or loss and R before fees. Saved as a draft; nothing is
              published.
            </p>
            <ResultsDisclaimer className="mt-1" />
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              Platform
              <select
                value={recapPlatform}
                onChange={(e) => setRecapPlatform(e.target.value as RecapPlatform)}
                className="px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="twitter">Twitter</option>
                <option value="instagram">Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="blog">Blog</option>
              </select>
            </label>
            <button
              onClick={handleDraftRecap}
              disabled={draftingRecap}
              className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {draftingRecap ? 'Drafting...' : "Draft today's recap"}
            </button>
          </div>
        </div>
        {recapMessage && (
          <p role="status" className="text-sm text-gray-700 mt-2">
            {recapMessage}
          </p>
        )}
      </div>

      {/* The auto-draft schedule is on the Automation tab: admin only */}
      {isAdmin && (
        <p className="text-xs text-gray-500" data-testid="autodraft-schedule-link">
          Auto-draft schedule:{' '}
          <Link to="/content/automation" className="font-medium text-primary-600 hover:underline">
            Content → Automation
          </Link>
        </p>
      )}

      <PostsSummary posts={posts} />

      {/* Drafts first, then published; each card one line until opened */}
      <ul className="space-y-2" aria-label="Content">
        {draftsFirst(contents).map((content, index) => (
          <li key={content.id}>
            <ContentCard
              content={content}
              expanded={expanded.has(content.id)}
              onToggle={() => toggle(content.id)}
              showRepurposeHint={index === 0}
              canPublish={isAdmin}
              showModel={isAdmin}
              onPublish={() => setConfirmingPublishId(content.id)}
              onRepurpose={() => setRepurposeContent(content)}
              onDelete={() => setConfirmingDelete(content)}
              posts={posts.filter((p) => p.content_id === content.id)}
              onPostYourself={() => setPostingContent(content)}
              onPostChange={(post) => setPosts((prev) => prev.map((p) => (p.id === post.id ? post : p)))}
              onPostRemove={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))}
              onSaved={(saved) => setContents((prev) => prev.map((c) => (c.id === saved.id ? saved : c)))}
            />
          </li>
        ))}
      </ul>

      {postingContent && (
        <PostYourselfDialog
          content={postingContent}
          onClose={() => setPostingContent(null)}
          onPosted={(post) => {
            setPosts((prev) => [post, ...prev])
            setPostingContent(null)
            open(post.content_id ?? postingContent.id)
            toast.success('Link saved. Its views and likes show on the post.')
          }}
        />
      )}

      {repurposeContent && (
        <RepurposeModal
          isOpen={true}
          onClose={() => setRepurposeContent(null)}
          content={repurposeContent}
        />
      )}

      <ConfirmDialog
        isOpen={confirmingPublishId !== null}
        title="Publish content"
        message="This will publish the post to FinCreator's account for its network (not your own accounts). Continue?"
        confirmLabel="Publish"
        confirming={publishingId !== null}
        onConfirm={handlePublish}
        onCancel={() => setConfirmingPublishId(null)}
      />

      <ConfirmDialog
        isOpen={confirmingDelete !== null}
        title="Delete content"
        message={
          confirmingDelete && (
            <>
              <p>
                Delete this <span className="capitalize">{confirmingDelete.content_type}</span> post?
              </p>
              <p className="mt-2 italic text-gray-500 line-clamp-2">“{firstLine(confirmingDelete)}”</p>
              <p className="mt-2">
                It is removed from your content list. Anything already published on a platform stays there.
              </p>
            </>
          )
        }
        confirmLabel="Delete"
        danger
        confirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(null)}
      />
    </div>
  )
}

/** The post's first non-empty line, markdown stripped, for identifying it. */
const firstLine = (content: ContentType): string => {
  const source = content.title || content.text || ''
  const line = source.split('\n').map((l) => l.trim()).find(Boolean) ?? ''
  return line.replace(/[#*_>`]/g, '').trim()
}

interface ContentCardProps {
  content: ContentType
  /** Open: the full post and its controls. Closed: one line. */
  expanded: boolean
  onToggle: () => void
  onPublish: () => void
  onRepurpose: () => void
  onDelete: () => void
  /** Explain Repurpose beside this card's button; set on the first card only. */
  showRepurposeHint?: boolean
  /** Admins only: publishing posts to FinCreator's house accounts. */
  canPublish?: boolean
  /** Admins only: which model wrote the post. */
  showModel?: boolean
  /** Where the creator posted it themselves, and how each post is doing. */
  posts?: CreatorPost[]
  onPostYourself?: () => void
  onPostChange?: (post: CreatorPost) => void
  onPostRemove?: (id: number) => void
  /** The draft was edited and saved. */
  onSaved?: (content: ContentType) => void
}

const STATUS_PILL: Record<string, string> = {
  published: 'bg-success-50 text-success-700',
  scheduled: 'bg-primary-50 text-primary-700',
  failed: 'bg-danger-50 text-danger-700',
}

/** "From Clips: episode-12.mp4, 00:12–00:40 · Open in Clips", on a draft sent from the Clips tab. */
const ClipsSourceLine = ({ source }: { source: ClipsSource }) => {
  const range =
    source.kind === 'clip' && source.start != null && source.end != null
      ? `, ${formatTime(source.start)}–${formatTime(source.end)}`
      : source.kind === 'episode'
        ? ', episode package'
        : ''
  return (
    <p className="mb-3 text-xs text-gray-500" data-testid="clips-source">
      From Clips: {source.filename}
      {range} ·{' '}
      <Link to={`/content/clips?video=${encodeURIComponent(source.video_id)}`} className="text-primary-600 underline">
        Open in Clips
      </Link>
    </p>
  )
}

const ContentCard = ({
  content,
  expanded,
  onToggle,
  onPublish,
  onRepurpose,
  onDelete,
  showRepurposeHint,
  canPublish = false,
  showModel = false,
  posts = [],
  onPostYourself,
  onPostChange = () => undefined,
  onPostRemove = () => undefined,
  onSaved,
}: ContentCardProps) => {
  const [editing, setEditing] = useState(false)
  // A published post stays as it went out.
  const editable = !!onSaved && content.status !== 'published'
  // A blog post is draft-only: there's no house account to publish it to.
  const publishable = canPublish && content.content_type !== 'blog'
  const bodyId = `content-${content.id}-body`

  return (
    <div className="bg-white rounded-lg shadow" data-testid="content-card" data-status={content.status}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={bodyId}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
        data-testid="content-card-summary"
      >
        <span className="w-20 shrink-0 text-xs font-medium uppercase text-gray-500">{content.content_type}</span>
        <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{firstLine(content) || '(empty)'}</span>
        <span
          className={`shrink-0 px-2 py-0.5 text-xs font-medium rounded ${STATUS_PILL[content.status] ?? 'bg-gray-100 text-gray-700'}`}
        >
          {content.status}
        </span>
        <span className="hidden w-28 shrink-0 text-right text-xs text-gray-500 sm:block">{cardDate(content)}</span>
        <span aria-hidden="true" className="shrink-0 text-gray-400">
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded && (
        <div id={bodyId} className="border-t px-4 pb-4 pt-3">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {/* A recap's date lives on the card; the post itself is undated. */}
            {content.recap_date && (
              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded" data-testid="recap-date">
                Recap · {content.recap_date}
              </span>
            )}
            {content.status === 'published' ? (
              <p className="text-sm text-success-700" data-testid="publish-state">
                {publishedLine(content)}
              </p>
            ) : content.status === 'failed' ? (
              <p className="text-sm text-danger-700" data-testid="publish-state">
                Publish failed — retry
              </p>
            ) : content.status === 'generated' ? (
              <p className="text-sm text-gray-500" data-testid="publish-state">
                Draft
              </p>
            ) : null}
          </div>

          <DraftFlags content={content} />

          {content.clips_source && <ClipsSourceLine source={content.clips_source} />}

          {content.title && <h3 className="font-semibold text-lg mb-2">{content.title}</h3>}

          {editing && onSaved ? (
            <DraftEditor
              content={content}
              onCancel={() => setEditing(false)}
              onSaved={(saved) => {
                onSaved(saved)
                setEditing(false)
              }}
            />
          ) : (
            <div
              className="text-gray-700 mb-4 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(content.text || '') }}
            />
          )}

          {content.hashtags && content.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {content.hashtags.slice(0, 5).map((tag, index) => (
                <span key={index} className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <PostedLinks posts={posts} onChange={onPostChange} onRemove={onPostRemove} />

          <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t">
            <div className="text-xs text-gray-500">
              {showModel && content.ai_model && !content.stand_in && <span data-testid="content-model">Model: {content.ai_model}</span>}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button onClick={onDelete} className="px-4 py-2 text-sm text-danger-700 rounded-md hover:bg-danger-50">
                Delete
              </button>

              {editable && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                >
                  Edit
                </button>
              )}

              <button
                onClick={onRepurpose}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                Repurpose
              </button>
              {showRepurposeHint && <Tooltip content={CONTENT_GLOSSARY.repurpose} label="Repurpose" />}

              {/* Anyone can post their own draft on their own account; a blog post is copied by hand. */}
              {onPostYourself && content.content_type !== 'blog' && (
                <button
                  onClick={onPostYourself}
                  className="px-4 py-2 border border-primary-600 text-primary-600 text-sm rounded-md hover:bg-primary-50"
                >
                  Post it yourself
                </button>
              )}

              {publishable && (content.status === 'generated' || content.status === 'failed') && (
                <button
                  onClick={onPublish}
                  className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
                >
                  {content.status === 'failed' ? 'Retry publish' : 'Publish'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Content
